import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { CookieConsent } from "@/components/CookieConsent";
import {
  CinematicHero,
  SectionHead,
  Reveal,
  FullBleedQuote,
  StatStripe,
  CinematicCTA,
  TripleStrip,
} from "@/components/service/Cinematic";
import alexanderSteinImage from "@/assets/alexander-stein.webp";
import heroImg from "@/assets/team-work.jpg";
import t2 from "@/assets/team-studio-2.webp";
import t4 from "@/assets/team-work-balcony.webp";
import t6 from "@/assets/team-ceiling-work.jpg";

const werte = [
  { title: "Meisterhandwerk", subtitle: "Eigenes Team", text: "Alle Arbeiten führen wir mit festem, eingespieltem Team aus. Keine Subunternehmer, kein Weiterreichen. Der Meister ist auf jeder Baustelle persönlich verantwortlich." },
  { title: "Ehrliche Beratung", subtitle: "Ohne Verkaufsdruck", text: "Wir empfehlen nur, was zu Ihrem Haus und Budget passt. Keine überteuerten Systeme, keine Upsells. Unser Ziel: Sie sind in 10 Jahren noch Kunde." },
  { title: "Sauberkeit", subtitle: "Standard, nicht Bonus", text: "Böden, Möbel und Pflanzen werden geschützt. Wir hinterlassen täglich eine aufgeräumte Baustelle. Das ist kein Versprechen – das ist Arbeitsstandard." },
  { title: "24h Reaktion", subtitle: "Verbindlich", text: "Anfragen beantworten wir innerhalb von 24 Stunden. Rückrufe halten wir ein, Termine halten wir. In unserem Handwerk leider keine Selbstverständlichkeit." },
];

const structuredData = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "mainEntity": {
    "@type": "LocalBusiness",
    "name": "Malerfachbetrieb Stein GmbH & Co. KG",
    "founder": { "@type": "Person", "name": "Alexander Stein", "jobTitle": "Malermeister & Geschäftsführer" },
    "foundingDate": "2014",
    "areaServed": "Herford",
  },
};

const UeberUns = () => (
  <div className="min-h-screen bg-background">
    <SEO
      title="Malerfachbetrieb in Herford: Meisterbetrieb seit 2014"
      description="Malermeister Alexander Stein in Herford: Meisterbetrieb seit 2014. Spezialist für Fassadendämmung, Schimmelschutz, energetische Sanierung & edle Oberflächen."
      keywords="Malermeister Herford, Malerfachbetrieb Stein, Alexander Stein Maler, Meisterbetrieb Herford, Maler Team Herford"
      canonical="https://malerfachbetrieb-stein.de/ueber-uns"
      structuredData={structuredData}
    />
    <Navigation />
    <WhatsAppButton />

    <main>
      <CinematicHero
        chapter="Kapitel 00 / Über uns"
        eyebrow="Meisterbetrieb seit 2014"
        title={<>Handwerk mit <em className="not-italic text-background/70">Handschrift</em>.</>}
        subtitle="Gegründet 2014 aus der Überzeugung, dass Handwerk besser geht: sauberer, präziser, ehrlicher. Meistergeführte Qualität in Herford und im gesamten Kreis – vom Innenraum bis zur energetischen Komplettsanierung."
        image={heroImg}
        imageAlt="Team Malerfachbetrieb Stein bei der Arbeit"
        ctaLabel="Persönliches Erstgespräch"
        compact
      />

      <StatStripe
        stats={[
          { value: "2014", label: "Gegründet" },
          { value: "10+", label: "Jahre Erfahrung" },
          { value: "4.7", label: "Google-Bewertung" },
          { value: "20+", label: "Gemeinden im Umkreis" },
        ]}
      />

      {/* Der Meister */}
      <section className="py-16 md:py-24 lg:py-32">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center max-w-6xl mx-auto">
            <Reveal className="lg:col-span-6 relative aspect-[3/4] overflow-hidden rounded-2xl bg-muted">
              <img
                src={alexanderSteinImage}
                alt="Alexander Stein – Malermeister & Geschäftsführer"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
            </Reveal>
            <div className="lg:col-span-6">
              <Reveal delay={60}>
                <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-4">
                  Der Meister
                </div>
              </Reveal>
              <Reveal delay={120}>
                <h2 className="font-caslon text-3xl md:text-5xl font-bold leading-tight mb-5">
                  Alexander Stein.
                </h2>
              </Reveal>
              <Reveal delay={180}>
                <p className="text-sm text-muted-foreground mb-6">Malermeister & Geschäftsführer</p>
              </Reveal>
              <Reveal delay={220}>
                <blockquote className="border-l-2 border-foreground/20 pl-6 my-8">
                  <p className="text-lg md:text-xl font-caslon italic leading-relaxed">
                    „Mir geht es nicht darum, Fassaden zu streichen. Mir geht es darum, dass Ihr Haus in 20 Jahren noch genauso gut aussieht – und energetisch auf der Höhe der Zeit ist."
                  </p>
                </blockquote>
              </Reveal>
              <Reveal delay={280}>
                <ul className="space-y-3 text-sm">
                  {[
                    "Malermeister – meistergeführter Betrieb",
                    "Gegründet 2014 als Malerfachbetrieb Stein GmbH & Co. KG",
                    "Schwerpunkt: Fassadendämmung, Innendämmung, Schimmelschutz, edle Oberflächen",
                    "Systempartner von Sto, Brillux und Caparol",
                    "Regional verwurzelt in Herford und im gesamten Kreis",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="mt-2 h-px w-4 bg-foreground/40 shrink-0" />
                      <span className="text-foreground/80">{item}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <FullBleedQuote
        image={t4}
        imageAlt="Team Malerfachbetrieb Stein – Fassadenarbeit"
        quote={<>„Eine Baustelle ist auch Visitenkarte. Sauberkeit, Ton und Termintreue sagen mehr als jeder Werbeflyer."</>}
        caption="Alexander Stein"
      />

      {/* Werte – dark grid */}
      <section className="py-16 md:py-24 lg:py-32 bg-foreground text-background">
        <div className="container mx-auto">
          <SectionHead
            invert
            eyebrow="Was uns ausmacht"
            title={<>Vier Haltungen,<br /> die alles bestimmen.</>}
          />
          <div className="grid md:grid-cols-2 gap-px bg-background/10">
            {werte.map((w, i) => (
              <Reveal key={w.title} delay={i * 100}>
                <div className="bg-foreground p-8 md:p-12 h-full">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-background/50 mb-3">
                    {String(i + 1).padStart(2, "0")} / {w.subtitle}
                  </div>
                  <h3 className="font-caslon text-3xl md:text-4xl font-bold mb-5">{w.title}</h3>
                  <p className="text-background/70 leading-relaxed">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Team im Einsatz */}
      <section className="py-16 md:py-24 lg:py-32">
        <div className="container mx-auto">
          <SectionHead
            eyebrow="Team im Einsatz"
            title="Wir bei der Arbeit."
            intro="Eigene Leute, eigene Verantwortung. Jede Baustelle wird vom Meister begleitet – vom ersten Aufmaß bis zur Endabnahme."
          />
        </div>
      </section>

      <TripleStrip
        images={[
          { src: t4, alt: "Team beim Anbringen von WDVS-Dämmplatten am Fassadengerüst", caption: "Fassadendämmung am Gerüst" },
          { src: t6, alt: "Mitarbeiter schleift Wand mit Festool-Schleifgerät", caption: "Wandflächen schleifen" },
          { src: t2, alt: "Team mit Airless-Spritzgerät und Werkzeugen", caption: "Maschinen & Werkzeuge" },
        ]}
      />

      {/* Partner */}
      <section className="py-16 border-y border-foreground/5 bg-muted/20">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12">
            <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
              Systempartner
            </span>
            <div className="flex items-center gap-10 md:gap-14">
              {["Sto", "Brillux", "Caparol"].map((p) => (
                <span key={p} className="font-caslon text-2xl md:text-3xl text-foreground/70">
                  {p}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CinematicCTA
        image={t2}
        imageAlt="Team Malerfachbetrieb Stein im Showroom"
        title={<>Lernen Sie uns<br /> persönlich kennen.</>}
        body="Kostenloses Erstgespräch vor Ort – unverbindlich, ohne Berechnung. Wir hören zu, bevor wir kalkulieren."
        ctaLabel="Termin anfragen"
      />
    </main>

    <Footer />
    <CookieConsent />
  </div>
);

export default UeberUns;
